import { useMemo, ReactNode } from "react";
import { useTranslation } from "@coworker/locales";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import CrossIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/CrossIconPath";
import TaskState from "@coworker/apprestructured/src/task-management/types/TaskState";
import ViewerTop from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/ViewerTop/ViewerTop";
import Texts from "@coworker/apprestructured/src/task-management/components/SheetTop/Texts/Texts";
import Subtitle from "@coworker/apprestructured/src/task-management/components/SheetTop/Subtitle/Subtitle";
import Title from "@coworker/apprestructured/src/task-management/components/SheetTop/Title/Title";
import CloseButtonWrapper from "@coworker/apprestructured/src/task-management/components/SheetTop/CloseButtonWrapper/CloseButtonWrapper";
import Badge from "@coworker/apprestructured/src/task-management/components/SheetTop/Badge/Badge";
import {
  calcDaysOverdue,
  calcOverdueString,
  formatDateTime,
} from "@coworker/apprestructured/src/task-management/util";
import { getByPath } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import {
  isLoading,
  Loading,
  LOADING,
} from "@coworker/apprestructured/src/task-management/loading";
import Priority from "../../../SheetTop/Priority/Priority";

type TaskViewerHeaderProps = {
  task: Record<string, unknown> | Loading;
  onCloseButtonClick: () => void;
  onMenuButtonClick: () => void;
  topFragment: ReactNode;
};

function TaskViewerHeader({
  task,
  onCloseButtonClick,
  onMenuButtonClick,
  topFragment,
}: TaskViewerHeaderProps) {
  const { t } = useTranslation();
  const title = getByPath<string>(task, "title");
  const created_at = getByPath<string>(task, "created_at");
  const completed_at = getByPath<string>(task, "completed_at");
  const closed_at = getByPath<string>(task, "closed_at");
  const start = getByPath<string | null>(task, "start");
  const state = getByPath<TaskState>(task, "state");
  const priority = getByPath<string>(task, "priority");

  const subtitle = useMemo(() => {
    if (
      isLoading(completed_at) ||
      isLoading(created_at) ||
      isLoading(closed_at) ||
      isLoading(start)
    ) {
      return LOADING;
    }
    if (state === "completed") {
      return `Completed ${formatDateTime(completed_at)}`;
    } else if (state === "closed") {
      return `Closed ${formatDateTime(closed_at)}`;
    } else if (start !== null) {
      return `Scheduled for ${start}`;
    } else {
      return `Created ${formatDateTime(created_at)}`;
    }
  }, [closed_at, completed_at, created_at, start, state]);

  const badge = useMemo(() => {
    if (
      isLoading(start) ||
      isLoading(state) ||
      start === null ||
      state !== "pending"
    ) {
      return null;
    }

    const daysOverdue = calcDaysOverdue(start);
    if (daysOverdue > 0) {
      return calcOverdueString(daysOverdue, t);
    } else {
      return null;
    }
  }, [start, state, t]);

  return (
    <>
      <ViewerTop state={state}>
        <CloseButtonWrapper>
          <FixaButton
            onClick={onCloseButtonClick}
            ssrIcon={CrossIconPath}
            iconOnly
            type="tertiary"
            inverseTheme
            size="small"
          />
        </CloseButtonWrapper>
        <Texts>
          {badge !== null && <Badge text={badge} />}
          <Title light onMenuButtonClick={onMenuButtonClick}>
            {isLoading(title) ? <>&nbsp</> : title}
          </Title>
          {!isLoading(priority) && priority && <Priority />}
          <Subtitle light>
            {isLoading(subtitle) ? <>&nbsp</> : subtitle}
          </Subtitle>
        </Texts>
      </ViewerTop>
      {topFragment}
    </>
  );
}

export default TaskViewerHeader;
